import { type ChangeEvent } from 'react';
import type { TourConfirmationType, TourDuration } from '@zg-rentals/ts-z-calendar-client';

import AvailabilityBody from './components/AvailabilityBody';
import BookingMethodBody from './components/BookingMethodBody';
import TimeZoneBody from './components/TimeZoneBody';
import TourDurationBody from './components/TourDurationBody';
import type { AvailabilityDate } from '../../../../types/TourAvailabilityCards';
import { EditListingTourConfigSteps } from '../../../../constants/EditListingTourConfigModalConstants';
import type { TimeZoneIds } from '../../../../constants/TourCalendar';

type EditListingTourConfigModalBodyProps = {
  availabilityDates: Array<AvailabilityDate>;
  isTouringEnabled: boolean;
  isTouringEnabledFromApi: boolean;
  step: EditListingTourConfigSteps;
  timeZone?: TimeZoneIds;
  tourConfirmationType?: TourConfirmationType;
  tourDuration?: TourDuration;
  onSelectBookingMethod: (tourConfirmationType: TourConfirmationType) => void;
  onSelectTourDuration: (tourDuration: TourDuration) => void;
  onSelectTimeZone: (timeZone: TimeZoneIds) => void;
  onSetAvailabilityDates: (availabilityDates: Array<AvailabilityDate>) => void;
  onToggleEnableTouring: (event: ChangeEvent<HTMLInputElement>) => void;
};

const EditListingTourConfigModalBody = ({
  availabilityDates,
  isTouringEnabled,
  isTouringEnabledFromApi,
  step,
  timeZone,
  tourConfirmationType,
  tourDuration,
  onSelectBookingMethod,
  onSelectTimeZone,
  onSelectTourDuration,
  onSetAvailabilityDates,
  onToggleEnableTouring,
}: EditListingTourConfigModalBodyProps) => {
  const isBookingMethodStep =
    step === EditListingTourConfigSteps.SET_BOOKING_METHOD || step === EditListingTourConfigSteps.EDIT_BOOKING_METHOD;
  const isAvailabilityStep =
    step === EditListingTourConfigSteps.SET_AVAILABILITY || step === EditListingTourConfigSteps.EDIT_AVAILABILITY;
  const isTourDurationStep =
    step === EditListingTourConfigSteps.EDIT_TOUR_DURATION || step === EditListingTourConfigSteps.SET_TOUR_DURATION;

  if (isBookingMethodStep) {
    return (
      <BookingMethodBody
        isTouringEnabled={isTouringEnabled}
        isTouringEnabledFromApi={isTouringEnabledFromApi}
        tourConfirmationType={tourConfirmationType}
        onSelectBookingMethod={onSelectBookingMethod}
        onToggleEnableTouring={onToggleEnableTouring}
      />
    );
  } else if (step === EditListingTourConfigSteps.SET_TIME_ZONE) {
    return <TimeZoneBody timeZone={timeZone} onSelectTimeZone={onSelectTimeZone} />;
  } else if (isTourDurationStep) {
    return <TourDurationBody tourDuration={tourDuration} onSelectTourDuration={onSelectTourDuration} />;
  } else if (isAvailabilityStep && timeZone) {
    return (
      <AvailabilityBody
        availabilityDates={availabilityDates}
        timeZone={timeZone}
        tourDuration={tourDuration}
        onAvailabilityDatesChange={onSetAvailabilityDates}
      />
    );
  }

  return null;
};

export default EditListingTourConfigModalBody;
